import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { MenuItem, TreeNode } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MultiSelectChangeEvent, MultiSelectFilterEvent } from 'primeng/multiselect';

import { ProductionInventoryService, ProductionViewMode } from '../../../services/base-actions/production-inventory.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';
import { DispatchInstructionProxyService } from '../../../proxies/inventory/dispatch-instruction.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { LabDipProxyService } from '../../../proxies/item/lab-dip.proxy.service';
import { applyLocationPaths, getDistinctLocationCodes } from '@card-software/nexus-utilities';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { LocalHelperService } from '../../../services/local-helper.service';
import { EnumService } from '../../../services/enum.service';
import { Translation } from '../../../models/translation';
import { LocationNode } from '../../../models/location';
import { RollSearchCommand } from '../../../models/production/roll-search';
import { Roll } from '../../../models/production/roll';
import {
  ProductionCardSearchCommandPaginated,
  ProductionCardResultItem,
  ProductionCardStatus,
  getProductionCardStatusLabel,
} from '../../../models/production/production-card-search';
import {
  FilterField,
  ProductionFilters,
  getFiltersForViewMode,
  filtersTranslations,
  filtersTooltipTranslations,
} from './production-filters.utils';
import { ProductionCardsProxyService } from '../../../proxies/production/production-card.proxy.service';
import { RollProxyService } from '../../../proxies/production/roll.proxy';

interface EnumOption {
  label: string;
  value: number;
}

interface AutocompleteOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-production-inventory-filters',
  templateUrl: './production-inventory-filters.component.html',
  styleUrl: './production-inventory-filters.component.scss',
})
export class ProductionInventoryFiltersComponent implements OnInit, OnDestroy {
  @Output() isLoading = new EventEmitter<number>();
  @Output() totalRecords = new EventEmitter<number>();

  // Location TreeSelect
  locations: LocationNode[] = [];
  locationTreeNodes: TreeNode[] = [];
  selectedLocationNodes: TreeNode[] = [];

  // Active filters currently displayed
  activeFilters: FilterField[] = [];

  // Available filters menu
  filtersItems: MenuItem[] = [];

  // Roll filters
  rollFilters: RollSearchCommand = {};

  // Production Card filters
  productionCardFilters: ProductionCardSearchCommandPaginated = {};

  // Production Card status options for multiselect
  statusOptions: EnumOption[] = [];
  selectedStatuses: ProductionCardStatus[] = [];

  // Roll enum options for multiselect
  rollStatusOptions: EnumOption[] = [];
  rollCategoryOptions: EnumOption[] = [];
  rollQualityOptions: EnumOption[] = [];
  selectedRollStatuses: number[] = [];
  selectedCategories: number[] = [];
  selectedQualities: number[] = [];

  // Text filter values (for display)
  filterValues: Record<string, string> = {};

  // Autocomplete options
  workOrderOptions: AutocompleteOption[] = [];
  productionCardOptions: AutocompleteOption[] = [];
  knittingOrderOptions: AutocompleteOption[] = [];
  dispatchInstructionOptions: AutocompleteOption[] = [];
  fabricOptions: AutocompleteOption[] = [];
  itemOptions: AutocompleteOption[] = [];
  labDipOptions: AutocompleteOption[] = [];

  // Language
  currentLanguage = 'en';

  // Debounce for search
  private searchSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private readonly DEBOUNCE_MS = 500;

  // Debounce for autocomplete searches
  private autocompleteSearchSubjects = new Map<ProductionFilters, Subject<string>>();
  private readonly AUTOCOMPLETE_DEBOUNCE_MS = 500;

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private productionCardProxyService: ProductionCardsProxyService,
    private rollProxyService: RollProxyService,
    private workOrderProxyService: WorkOrderProxyService,
    private knittingOrderProxyService: KnittingOrderProxyService,
    private dispatchInstructionProxyService: DispatchInstructionProxyService,
    private fabricProxyService: FabricProxyService,
    private itemMasterProxyService: ItemMasterProxyService,
    private labDipProxyService: LabDipProxyService,
    private locationProxyService: LocationProxyService,
    private translateService: TranslateService,
    public localHelperTranslate: LocalHelperService,
    private enumService: EnumService,
  ) {
    this.currentLanguage = this.translateService.currentLang || 'en';
    this.translateService.onLangChange.subscribe((event) => {
      this.currentLanguage = event.lang;
      this.updateFiltersItems();
      this.initEnumOptions();
    });
  }

  ngOnInit(): void {
    this.updateFiltersItems();
    this.initEnumOptions();
    this.setupSearchDebounce();
    this.setupAutocompleteDebounce();
    // Load locations for TreeSelect
    this.getLocations();
    // Load initial autocomplete options
    this.loadInitialAutocompleteOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchDebounce(): void {
    this.searchSubject.pipe(takeUntil(this.destroy$), debounceTime(this.DEBOUNCE_MS)).subscribe(() => {
      this.executeSearch();
    });
  }

  private setupAutocompleteDebounce(): void {
    const filterKeys: ProductionFilters[] = [
      'workOrderReferences',
      'productionCardReferences',
      'knittingOrderReferences',
      'dispatchInstructionReferences',
      'fabricCodes',
      'itemCodes',
      'colorReferences',
    ];

    filterKeys.forEach((key) => {
      const subject = new Subject<string>();
      this.autocompleteSearchSubjects.set(key, subject);

      subject.pipe(takeUntil(this.destroy$), debounceTime(this.AUTOCOMPLETE_DEBOUNCE_MS)).subscribe((query) => {
        this.executeAutocompleteSearch(key, query);
      });
    });
  }

  private loadInitialAutocompleteOptions(): void {
    this.getWorkOrders('');
    this.getProductionCards('');
    this.getKnittingOrders('');
    this.getDispatchInstructions('');
    this.getFabrics('');
    this.getItems('');
    this.getLabDips('');
  }

  private executeAutocompleteSearch(key: ProductionFilters, query: string): void {
    switch (key) {
      case 'workOrderReferences':
        this.getWorkOrders(query);
        break;
      case 'productionCardReferences':
        this.getProductionCards(query);
        break;
      case 'knittingOrderReferences':
        this.getKnittingOrders(query);
        break;
      case 'dispatchInstructionReferences':
        this.getDispatchInstructions(query);
        break;
      case 'fabricCodes':
        this.getFabrics(query);
        break;
      case 'itemCodes':
        this.getItems(query);
        break;
      case 'colorReferences':
        this.getLabDips(query);
        break;
    }
  }

  get currentViewMode(): ProductionViewMode {
    return this.productionInventoryService.viewMode();
  }

  getTranslation(key: string): string {
    return filtersTranslations[key]?.[this.currentLanguage as keyof Translation] || key;
  }

  getTooltip(key: string): string {
    return filtersTooltipTranslations[key]?.[this.currentLanguage as keyof Translation] || this.getTranslation(key);
  }

  // *** Location TreeSelect Methods *** //
  getLocations(): void {
    this.locationProxyService.getAll().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.locations = response.data;
          this.locationTreeNodes = this.buildLocationTreeStructure(response.data);
          this.triggerSearch(true);
        }
      },
      error: (error) => {
        console.error('Error loading locations:', error);
        this.triggerSearch(true);
      },
    });
  }

  /**
   * Build tree structure from flat location list using parentCode relationships
   */
  private buildLocationTreeStructure(locations: LocationNode[]): TreeNode[] {
    if (!locations || locations.length === 0) {
      return [];
    }

    const locationMap = new Map<string, TreeNode>();
    const rootNodes: TreeNode[] = [];

    // First pass: create all tree nodes
    locations.forEach((location) => {
      locationMap.set(location.locationCode, {
        key: location.locationCode,
        label: `${location.code} - ${this.localHelperTranslate.getLocalizedValue(location.name)}`,
        data: location,
        children: [],
        expanded: false,
        selectable: true,
      });
    });

    // Second pass: build the hierarchy using parentCode
    locations.forEach((location) => {
      const treeNode = locationMap.get(location.locationCode);
      if (!treeNode) return;

      if (location.parentCode) {
        const parentNode = locationMap.get(location.parentCode);
        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }
          parentNode.children.push(treeNode);
        } else {
          rootNodes.push(treeNode);
        }
      } else {
        rootNodes.push(treeNode);
      }
    });

    return rootNodes;
  }

  /**
   * Resolves the full location path for the given rolls by calling the Inventory location endpoint
   * with the distinct CurrentLocationCodes. The API returns each location's enriched FullName, which
   * we map back onto each roll. Done straight from the UI to Inventory (the same path Inventory
   * Sessions uses), so it avoids the Production→Inventory hop and keeps the enrichment on the API.
   */
  private enrichRollLocationNames(rolls: Roll[]): void {
    const codes = getDistinctLocationCodes(rolls, (r) => r.currentLocationCode);
    if (codes.length === 0) return;

    this.locationProxyService.batchGetByCodes(codes, true).subscribe({
      next: (response) => {
        if (!response?.data) return;
        applyLocationPaths(
          rolls,
          response.data,
          (r) => r.currentLocationCode,
          (roll, location) => {
            // pathNames drives the display (leaf styled separately); fullName kept as a plain-string fallback.
            if (location.fullName) roll.currentLocationName = location.fullName;
            roll.currentLocationPathNames = location.pathNames!;
          },
        );
        // Re-set to trigger change detection now that the roll items were enriched.
        this.productionInventoryService.setRollResults([...rolls]);
      },
      error: (error) => console.error('Error resolving roll location names:', error),
    });
  }

  /**
   * Handle TreeSelect location selection change
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onLocationTreeSelectChange(event: unknown): void {
    this.applyLocationFilter();
    this.triggerSearch(true);
  }

  private applyLocationFilter(): void {
    const selectedNodes: TreeNode[] = this.selectedLocationNodes || [];

    const locationCodes: string[] = selectedNodes
      .map((node) => {
        const location = node.data as LocationNode;
        return location.locationCode;
      })
      .filter((code) => code !== undefined);

    if (this.currentViewMode === 'roll') {
      if (locationCodes.length === 0) {
        delete this.rollFilters.locationCodes;
      } else {
        this.rollFilters = { ...this.rollFilters, locationCodes };
      }
    } else {
      if (locationCodes.length === 0) {
        delete this.productionCardFilters.locationCodes;
      } else {
        this.productionCardFilters = { ...this.productionCardFilters, locationCodes };
      }
    }
  }

  private initEnumOptions(): void {
    // Production Card statuses
    const statuses = [
      ProductionCardStatus.Draft,
      ProductionCardStatus.Submitted,
      ProductionCardStatus.Approved,
      ProductionCardStatus.PlanningForBatching,
      ProductionCardStatus.InRouteToBatching,
      ProductionCardStatus.Batching,
      ProductionCardStatus.InRouteToBrd,
      ProductionCardStatus.Brd,
      ProductionCardStatus.Dyeing,
      ProductionCardStatus.ReadyForFinishing,
      ProductionCardStatus.Finishing,
      ProductionCardStatus.FinishedGood,
      ProductionCardStatus.Shipped,
    ];

    this.statusOptions = statuses.map((status) => ({
      label: getProductionCardStatusLabel(status)[this.currentLanguage as keyof Translation] || status.toString(),
      value: status,
    }));

    // Roll statuses from enum service
    const rollStatusEnum = this.enumService.getEnum('RollStatus', 'production').values;
    this.rollStatusOptions = Object.entries(rollStatusEnum).map(([key, label]) => ({
      label: label,
      value: parseInt(key, 10),
    }));

    // Roll categories from enum service
    const rollCategoryEnum = this.enumService.getEnum('RollCategory', 'production').values;
    this.rollCategoryOptions = Object.entries(rollCategoryEnum).map(([key, label]) => ({
      label: label,
      value: parseInt(key, 10),
    }));

    // Roll qualities from enum service
    const rollQualityEnum = this.enumService.getEnum('RollQuality', 'production').values;
    this.rollQualityOptions = Object.entries(rollQualityEnum).map(([key, label]) => ({
      label: label,
      value: parseInt(key, 10),
    }));
  }

  updateFiltersItems(): void {
    const availableFilters = getFiltersForViewMode(this.currentViewMode, this.translateService);
    const activeFilterNames = this.activeFilters.map((f) => f.name);

    this.filtersItems = availableFilters
      .filter((filter) => !activeFilterNames.includes(filter.name))
      .map((filter) => ({
        label: this.getTranslation(filter.name),
        icon: 'pi pi-filter',
        command: () => this.addFilter(filter),
      }));
  }

  addFilter(filter: FilterField): void {
    if (!this.activeFilters.find((f) => f.name === filter.name)) {
      this.activeFilters.push(filter);
      this.updateFiltersItems();
    }
  }

  removeFilter(filter: FilterField): void {
    this.activeFilters = this.activeFilters.filter((f) => f.name !== filter.name);
    this.clearFilterValue(filter.name);
    this.updateFiltersItems();
    this.triggerSearch(true);
  }

  private clearFilterValue(filterName: ProductionFilters): void {
    delete this.filterValues[filterName];

    if (this.currentViewMode === 'roll') {
      // Clear roll enum selections
      if (filterName === 'rollStatuses') {
        this.selectedRollStatuses = [];
        this.rollFilters.statuses = undefined;
      } else if (filterName === 'categories') {
        this.selectedCategories = [];
        this.rollFilters.categories = undefined;
      } else if (filterName === 'qualities') {
        this.selectedQualities = [];
        this.rollFilters.qualities = undefined;
      } else {
        (this.rollFilters as Record<string, unknown>)[filterName] = undefined;
      }
    } else {
      if (filterName === 'statuses') {
        this.selectedStatuses = [];
      }
      (this.productionCardFilters as Record<string, unknown>)[filterName] = undefined;
    }
  }

  onFilterValueChange(filterName: ProductionFilters, value: string): void {
    this.filterValues[filterName] = value;

    if (this.currentViewMode === 'roll') {
      if (value && value.trim()) {
        (this.rollFilters as Record<string, unknown>)[filterName] = [value.trim()];
      } else {
        (this.rollFilters as Record<string, unknown>)[filterName] = undefined;
      }
    } else {
      if (value && value.trim()) {
        (this.productionCardFilters as Record<string, unknown>)[filterName] = [value.trim()];
      } else {
        (this.productionCardFilters as Record<string, unknown>)[filterName] = undefined;
      }
    }

    this.resetSearchPage();
    this.searchSubject.next();
  }

  onStatusChange(): void {
    if (this.selectedStatuses && this.selectedStatuses.length > 0) {
      this.productionCardFilters.statuses = this.selectedStatuses;
    } else {
      this.productionCardFilters.statuses = undefined;
    }
    this.triggerSearch(true);
  }

  // Roll enum change handlers
  onRollStatusChange(): void {
    if (this.selectedRollStatuses && this.selectedRollStatuses.length > 0) {
      this.rollFilters.statuses = this.selectedRollStatuses;
    } else {
      this.rollFilters.statuses = undefined;
    }
    this.triggerSearch(true);
  }

  onCategoryChange(): void {
    if (this.selectedCategories && this.selectedCategories.length > 0) {
      this.rollFilters.categories = this.selectedCategories;
    } else {
      this.rollFilters.categories = undefined;
    }
    this.triggerSearch(true);
  }

  onQualityChange(): void {
    if (this.selectedQualities && this.selectedQualities.length > 0) {
      this.rollFilters.qualities = this.selectedQualities;
    } else {
      this.rollFilters.qualities = undefined;
    }
    this.triggerSearch(true);
  }

  // Autocomplete change handlers
  onAutocompleteChange(key: ProductionFilters, event: MultiSelectChangeEvent): void {
    const value = event.value.length === 0 ? undefined : event.value;

    if (this.currentViewMode === 'roll') {
      if (value === undefined) {
        delete (this.rollFilters as Record<string, unknown>)[key];
      } else {
        (this.rollFilters as Record<string, unknown>)[key] = value;
      }
    } else {
      if (value === undefined) {
        delete (this.productionCardFilters as Record<string, unknown>)[key];
      } else {
        (this.productionCardFilters as Record<string, unknown>)[key] = value;
      }
    }

    this.resetSearchPage();
    this.searchSubject.next();
  }

  searchAutocompleteOptions(key: ProductionFilters, event: MultiSelectFilterEvent): void {
    const query = event.filter;
    const subject = this.autocompleteSearchSubjects.get(key);
    if (subject) {
      subject.next(query);
    }
  }

  getFilterOptions(key: ProductionFilters): AutocompleteOption[] {
    switch (key) {
      case 'workOrderReferences':
        return this.workOrderOptions;
      case 'productionCardReferences':
        return this.productionCardOptions;
      case 'knittingOrderReferences':
        return this.knittingOrderOptions;
      case 'dispatchInstructionReferences':
        return this.dispatchInstructionOptions;
      case 'fabricCodes':
        return this.fabricOptions;
      case 'itemCodes':
        return this.itemOptions;
      case 'colorReferences':
        return this.labDipOptions;
      default:
        return [];
    }
  }

  // Data fetching methods for autocomplete
  private getWorkOrders(query: string): void {
    this.workOrderProxyService.search({ orderNumber: query }, 1, 50, 'orderNumber', 1).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const options = response.data.items.map((wo) => ({
            id: wo.id!,
            name: wo.orderNumber || wo.id!,
          }));
          this.workOrderOptions = this.deduplicateOptions(options);
          this.preserveExistingSelections('workOrderReferences');
        }
      },
      error: (error) => {
        console.error('Error searching work orders:', error);
      },
    });
  }

  private getProductionCards(query: string): void {
    const command: ProductionCardSearchCommandPaginated = query ? { productionCardNumbers: [query] } : {};
    this.productionCardProxyService.searchPaginated(command, 1, 50).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const options = (response.data.items as unknown as ProductionCardResultItem[])
            .map((pc) => ({
              id: pc.productionCard?.reference || '',
              name: pc.productionCard?.number || '',
            }))
            .filter((pc) => !!pc.id && !!pc.name);
          this.productionCardOptions = this.deduplicateOptions(options);
          this.preserveExistingSelections('productionCardReferences');
        }
      },
      error: (error) => {
        console.error('Error searching production cards:', error);
      },
    });
  }

  private getKnittingOrders(query: string): void {
    this.knittingOrderProxyService.search({ knittingOrderNumber: query }, 1, 50).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const options = response.data.items.map((ko) => ({
            id: ko.id,
            name: ko.knittingOrderNumber,
          }));
          this.knittingOrderOptions = this.deduplicateOptions(options);
          this.preserveExistingSelections('knittingOrderReferences');
        }
      },
      error: (error) => {
        console.error('Error searching knitting orders:', error);
      },
    });
  }

  private getDispatchInstructions(query: string): void {
    this.dispatchInstructionProxyService.search({ number: query }).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          const options = response.data.items.map((di) => ({
            id: di.id!,
            name: di.number || di.id!,
          }));
          this.dispatchInstructionOptions = this.deduplicateOptions(options);
          this.preserveExistingSelections('dispatchInstructionReferences');
        }
      },
      error: (error) => {
        console.error('Error searching dispatch instructions:', error);
      },
    });
  }

  private getFabrics(query: string): void {
    this.fabricProxyService.search({ fabricCode: query, longDescription: query }, true).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          const options = response.data?.items.map((fabric) => ({
            id: fabric.fabricCode!,
            name: `(${fabric.fabricCode}) ${fabric.longDescription || ''}`,
          }));
          this.fabricOptions = this.deduplicateOptions(options);
          this.preserveExistingSelections('fabricCodes');
        }
      },
      error: (error) => {
        console.error('Error searching fabrics:', error);
      },
    });
  }

  private getItems(query: string): void {
    this.itemMasterProxyService.search({ name: query, itemCode: query }, true, false).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          const options = response.data?.items.map((item) => ({
            id: item.itemCode,
            name: `(${item.itemCode}) ${this.localHelperTranslate.getLocalizedValue(item.itemDisplayName)}`,
          }));
          this.itemOptions = this.deduplicateOptions(options);
          this.preserveExistingSelections('itemCodes');
        }
      },
      error: (error) => {
        console.error('Error searching items:', error);
      },
    });
  }

  private getLabDips(query: string): void {
    this.labDipProxyService.search({ labDipNumber: query || undefined }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const options = response.data?.items.map((ld) => ({
            id: ld.colorReference,
            name: `${ld.labDipNumber} - ${ld.colorDto?.name || ''}`,
          }));
          this.labDipOptions = this.deduplicateOptions(options);
          this.preserveExistingSelections('colorReferences');
        }
      },
      error: (error) => {
        console.error('Error searching lab dips:', error);
      },
    });
  }

  private deduplicateOptions(options: AutocompleteOption[]): AutocompleteOption[] {
    const seen = new Set<string>();
    return options.filter((opt) => {
      if (seen.has(opt.id)) {
        return false;
      }
      seen.add(opt.id);
      return true;
    });
  }

  private preserveExistingSelections(filterKey: ProductionFilters): void {
    const currentFilters = this.currentViewMode === 'roll' ? this.rollFilters : this.productionCardFilters;
    const currentSelections = (currentFilters as Record<string, unknown>)[filterKey];

    if (currentSelections && Array.isArray(currentSelections)) {
      const currentOptions = this.getFilterOptions(filterKey);
      const existingIds = new Set(currentOptions.map((opt) => opt.id));

      currentSelections.forEach((selected) => {
        if (!existingIds.has(selected)) {
          currentOptions.push({
            id: selected,
            name: selected,
          });
          existingIds.add(selected);
        }
      });
    }
  }

  triggerSearch(resetPage = false): void {
    if (resetPage) {
      this.resetSearchPage();
    }
    this.executeSearch();
  }

  private resetSearchPage(): void {
    this.productionInventoryService.rollSearchPage.set(1);
    this.productionInventoryService.productionCardSearchPage.set(1);
  }

  private executeSearch(): void {
    this.isLoading.emit(1);

    if (this.currentViewMode === 'roll') {
      this.searchRolls();
    } else {
      this.searchProductionCards();
    }
  }

  private searchRolls(): void {
    const page = this.productionInventoryService.rollSearchPage();
    const pageSize = this.productionInventoryService.rollSearchPageSize();

    this.rollProxyService.searchRoll(this.rollFilters, page, pageSize).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const rolls = response.data.items;
          this.productionInventoryService.setRollResults(rolls);
          this.totalRecords.emit(response.data.totalCount);
          // Resolve the full location path via the Inventory endpoint (distinct CurrentLocationCodes).
          this.enrichRollLocationNames(rolls);
        } else {
          this.productionInventoryService.setRollResults([]);
          this.totalRecords.emit(0);
        }
        this.isLoading.emit(-1);
      },
      error: (error) => {
        console.error('Error searching rolls:', error);
        this.productionInventoryService.setRollResults([]);
        this.totalRecords.emit(0);
        this.isLoading.emit(-1);
      },
    });
  }

  private searchProductionCards(): void {
    const page = this.productionInventoryService.productionCardSearchPage();
    const pageSize = this.productionInventoryService.productionCardSearchPageSize();

    this.productionCardProxyService.searchPaginated(this.productionCardFilters, page, pageSize).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.productionInventoryService.setProductionCardResults(response.data.items as unknown as ProductionCardResultItem[]);
          this.totalRecords.emit(response.data.totalCount);
        } else {
          this.productionInventoryService.setProductionCardResults([]);
          this.totalRecords.emit(0);
        }
        this.isLoading.emit(-1);
      },
      error: (error) => {
        console.error('Error searching production cards:', error);
        this.productionInventoryService.setProductionCardResults([]);
        this.totalRecords.emit(0);
        this.isLoading.emit(-1);
      },
    });
  }

  getFilterValue(filterName: ProductionFilters): string {
    return this.filterValues[filterName] || '';
  }

  hasFilterValue(filterName: ProductionFilters): boolean {
    // Production card statuses
    if (filterName === 'statuses') {
      return this.selectedStatuses && this.selectedStatuses.length > 0;
    }

    // Roll enum filters
    if (filterName === 'rollStatuses') {
      return this.selectedRollStatuses && this.selectedRollStatuses.length > 0;
    }
    if (filterName === 'categories') {
      return this.selectedCategories && this.selectedCategories.length > 0;
    }
    if (filterName === 'qualities') {
      return this.selectedQualities && this.selectedQualities.length > 0;
    }

    // For autocomplete filters, check if there are selected values
    if (this.isAutocompleteFilter(filterName)) {
      return this.getSelectedCount(filterName) > 0;
    }

    return !!this.filterValues[filterName];
  }

  isAutocompleteFilter(filterName: ProductionFilters): boolean {
    return [
      'workOrderReferences',
      'productionCardReferences',
      'knittingOrderReferences',
      'dispatchInstructionReferences',
      'fabricCodes',
      'itemCodes',
      'colorReferences',
    ].includes(filterName);
  }

  isEnumFilter(filterName: ProductionFilters): boolean {
    return ['statuses', 'rollStatuses', 'categories', 'qualities'].includes(filterName);
  }

  getSelectedCount(filterName: ProductionFilters): number {
    // Production card statuses
    if (filterName === 'statuses') {
      return this.selectedStatuses?.length || 0;
    }

    // Roll enum filters
    if (filterName === 'rollStatuses') {
      return this.selectedRollStatuses?.length || 0;
    }
    if (filterName === 'categories') {
      return this.selectedCategories?.length || 0;
    }
    if (filterName === 'qualities') {
      return this.selectedQualities?.length || 0;
    }

    // For autocomplete filters
    if (this.isAutocompleteFilter(filterName)) {
      const currentFilters = this.currentViewMode === 'roll' ? this.rollFilters : this.productionCardFilters;
      const values = (currentFilters as Record<string, unknown>)[filterName];
      if (Array.isArray(values)) {
        return values.length;
      }
      return 0;
    }

    return this.filterValues[filterName] ? 1 : 0;
  }

  clearAllFilters(): void {
    this.activeFilters = [];
    this.filterValues = {};
    this.rollFilters = {};
    this.productionCardFilters = {};
    this.selectedStatuses = [];
    this.selectedRollStatuses = [];
    this.selectedCategories = [];
    this.selectedQualities = [];
    this.selectedLocationNodes = [];
    this.updateFiltersItems();
    this.triggerSearch(true);
  }

  onViewModeChanged(): void {
    // Clear filters when view mode changes
    this.activeFilters = [];
    this.filterValues = {};
    this.rollFilters = {};
    this.productionCardFilters = {};
    this.selectedStatuses = [];
    this.selectedRollStatuses = [];
    this.selectedCategories = [];
    this.selectedQualities = [];
    this.updateFiltersItems();
    this.triggerSearch(true);
  }
}
